import { toast } from "react-toastify";
import create from "zustand";
import { generateFormLogic } from "../../../helpers/generate-form-logic";
import {
  FormAction,
  FormInput,
  FormProps,
  SelectData,
} from "../../../interfaces/form-action-interfaces";
import {
  GridProductData,
  GridProductResultInterface,
} from "../interfaces/product-result.interface";
import { GridProductSingleResultInterface } from "../interfaces/product-single-result.interface";
import {
  DeleteGridProduct,
  GetAllGridProduct,
  GetAllGridProductParam,
  ShowGridProduct,
  SubmitGridProduct,
  UpdateGridProduct,
} from "../providers/product.provider";

interface GridProductState {
  productModel?: GridProductResultInterface;
  products: GridProductData[];
  productsSelectData: SelectData[];
  productLoading: boolean;
  onDeleteGridProduct: (id: string, params: GetAllGridProductParam) => void;
  getGridProducts: (params: GetAllGridProductParam) => void;
  searchForms: FormInput[];
  productParams: GetAllGridProductParam;
  setGridProductParams: (data: GetAllGridProductParam) => void;
  setSearchForms: (index: number, value: any) => void;
  setSearchFormSelectData: (index: number, selectData: SelectData[]) => void;
}

export const useGridProductStore = create<GridProductState>((set, get) => ({
  productLoading: false,
  productModel: undefined,
  products: [],
  productsSelectData: [],
  setGridProductParams: (data: GetAllGridProductParam) => {
    set({ productParams: data });
  },
  productParams: {
    page: 0,
    limit: 10,
  },
  searchForms: [
    {
      title: "GridProduct Name",
      placeholder: "GridProduct Name...",
      type: "text",
      name: "name",
      value: "",
      disabled: false,
    },
    {
      title: "Brand",
      placeholder: "Brand...",
      type: "select-with-text",
      name: "brandId",
      selectData: [],
      value: {
        label: "Choose brand",
        value: "",
      },
      disabled: false,
    },
  ],
  setSearchForms: (index: number, value: any) => {
    const forms = [...get().searchForms];
    forms[index] = {
      ...forms[index],
      value: value,
    };
    set({ searchForms: forms });
  },
  setSearchFormSelectData: (index: number, selectData: SelectData[]) => {
    const newSelectData: SelectData[] = [
      {
        label: "Choose brand...",
        value: "",
      },
    ];
    newSelectData.push(...selectData);
    const forms = [...get().searchForms];
    forms[index] = {
      ...forms[index],
      selectData: newSelectData,
    };
    set({ searchForms: forms });
  },
  getGridProducts: async (params: GetAllGridProductParam) => {
    set({ productLoading: true });
    const response = await GetAllGridProduct(params);
    if (response) {
      if (response.status) {
        set({ productModel: response });
        if (response.data.data.length > 0) {
          let transformGridProducts: GridProductData[] = [];
          let transformSelectDatas: SelectData[] = [];
          for (const item of response.data.data) {
            transformGridProducts.push({
              id: item.id,
              name: item.name,
              // slug: item.slug,
              brandName: item.brand.name,
              media:
                item.medias.length > 0
                  ? item.medias[0]
                  : "9961f37f-63b5-4506-b972-872f0f272b07",
            });
            transformSelectDatas.push({
              value: item.id,
              label: item.name,
            });
          }
          set({
            products: transformGridProducts,
            productsSelectData: transformSelectDatas,
          });
        } else {
          set({ products: [], productsSelectData: [] });
        }
      } else {
        set({ products: [], productsSelectData: [] });
      }
    } else {
      set({ products: [], productsSelectData: [] });
    }
    set({ productLoading: false });
  },
  onDeleteGridProduct: async (id: string, params: GetAllGridProductParam) => {
    const response = await DeleteGridProduct(id);
    if (response.status) {
      get().getGridProducts(params);
      toast.success(response.message);
    } else {
      toast.error(response.message);
    }
  },
}));

interface GridProductFormState {
  forms?: FormInput[];
  setForms?: (index: number, value: any) => void;
  setFormSelectData: (index: number, selectData: SelectData[]) => void;
  loading: boolean;
  formLoading?: boolean;
  submitDone?: boolean;
  data?: GridProductSingleResultInterface;
  generateForms: (formParam: FormProps) => void;
  onSubmitForm: () => void;
  fetchSingleData: (id: string) => void;
  formParam?: FormProps;
  setFormParam?: (formParam: FormProps) => void;
  reset: () => void;
}

const formInitialState = {
  loading: false,
  forms: [],
  data: undefined,
  formParam: undefined,
  formLoading: undefined,
  submitDone: undefined,
};

export const useGridProductFormStore = create<GridProductFormState>(
  (set, get) => ({
    loading: false,
    forms: [],
    data: undefined,
    formParam: undefined,
    setForms: (index: number, value: any) => {
      const forms = [...get().forms];
      forms[index] = {
        ...forms[index],
        value: value,
      };
      set({ forms: forms });
    },
    setFormSelectData: (index: number, selectData: SelectData[]) => {
      const forms = [...get().forms];
      forms[index] = {
        ...forms[index],
        selectData: selectData,
      };
      set({ forms: forms });
    },
    setFormParam: (formParam: FormProps) => {
      set({ formParam: formParam });
    },
    fetchSingleData: async (id: string) => {
      set({ loading: true });
      if (id !== undefined || id !== null) {
        const response = await ShowGridProduct(id);
        set({ data: response });
      } else {
        set({ data: undefined });
      }
      set({ loading: false });
      return;
    },
    generateForms: async (formParam: FormProps) => {
      set({ forms: [], formParam: formParam });

      let data: GridProductData = undefined;
      if (
        formParam.action === FormAction.VIEW ||
        formParam.action === FormAction.UPDATE
      ) {
        const response = await ShowGridProduct(formParam.id);
        if (response.status) {
          data = response.data;
        }
      }
      let forms: FormInput[] = [];

      forms = [
        {
          title: "Product Photo",
          placeholder: "Product Photo...",
          type: "file-upload-multi",
          name: "medias",
          value: data?.medias ?? [],
          disabled: formParam.action === FormAction.VIEW ? true : false,
        },
        {
          title: "Name",
          placeholder: "Name...",
          type: "text",
          name: "name",
          value: data?.name ?? "",
          disabled: formParam.action === FormAction.VIEW ? true : false,
        },
        {
          title: "Description",
          placeholder: "Description...",
          type: "rich-text",
          name: "description",
          value: data?.description ?? "",
          disabled: formParam.action === FormAction.VIEW ? true : false,
        },
        {
          title: "Price",
          placeholder: "Price...",
          type: "number",
          name: "price",
          value: data?.price ?? "",
          disabled: formParam.action === FormAction.VIEW ? true : false,
        },
        {
          title: "Brand",
          placeholder: "Brand...",
          type: "select-with-text",
          name: "brandId",
          selectData: [],
          value: {
            label: data?.brand?.name,
            value: data?.brand?.id,
          },
          disabled: formParam.action === FormAction.VIEW ? true : false,
        },
        {
          title: "Extra Description",
          placeholder: "Extra Description...",
          type: "rich-text",
          name: "extra_description",
          value: data?.extra_description ?? "",
          disabled: formParam.action === FormAction.VIEW ? true : false,
        },
        {
          title: "Reference Number",
          placeholder: "Reference Number...",
          type: "text",
          name: "reference_number",
          value: data?.reference_number ?? "",
          disabled: formParam.action === FormAction.VIEW ? true : false,
        },
        {
          title: "Engine Type",
          placeholder: "Engine Type...",
          type: "text",
          name: "engine_type",
          value: data?.engine_type ?? "",
          disabled: formParam.action === FormAction.VIEW ? true : false,
        },
        {
          title: "Tokopedia Link",
          placeholder: "Tokopedia Link...",
          type: "text",
          name: "tokopediaLink",
          value: data?.tokopediaLink ?? "",
          disabled: formParam.action === FormAction.VIEW ? true : false,
        },
        {
          title: "Shopee Link",
          placeholder: "Shopee Link...",
          type: "text",
          name: "shopeeLink",
          value: data?.shopeeLink ?? "",
          disabled: formParam.action === FormAction.VIEW ? true : false,
        },
      ];
      set({ forms: forms });
    },
    onSubmitForm: async () => {
      // let formData = {};
      // for (const v of get().forms) {
      //   formData = {
      //     ...formData,
      //     [v.name]: generateFormLogic(v.type, v?.value ?? ''),
      //   };
      // }
      // console.log(formData);
      // return;
      try {
        set({ formLoading: true, submitDone: false });
        if (get().formParam.action === FormAction.VIEW) {
          set({ formLoading: false, submitDone: false });
          return toast.error("Cant submit form, because its read-only");
        }

        set({ loading: true });

        let formData = {};
        for (const v of get().forms) {
          formData = {
            ...formData,
            [v.name]: generateFormLogic(v.type, v?.value ?? ""),
          };
        }

        let res: GridProductSingleResultInterface = {};

        if (get().formParam.action === FormAction.CREATE)
          res = await SubmitGridProduct(formData);
        if (get().formParam.action === FormAction.UPDATE)
          res = await UpdateGridProduct(get().formParam.id, formData);

        console.log(res.status);
        if (res.status) {
          toast.success(res.message);
          set({ submitDone: true, formLoading: false });
          return;
        } else {
          if (Array.isArray(res.message)) {
            for (const error of res.message) {
              toast.error(error);
            }
          } else {
            toast.error(res.message);
          }
          set({ submitDone: false, formLoading: false });
          return;
        }
      } catch (e) {
        console.log(e);
        toast.error(e);
        set({ formLoading: false, submitDone: false });
        return;
      }
    },
    reset: () => {
      set(formInitialState);
      return;
    },
  })
);
